import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box, Breadcrumbs, Button, Checkbox, Chip, FormControlLabel, Grid, IconButton, Link,
    MenuItem, Pagination, Select, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Tooltip, Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import OpaqueLoading from "../../components/opaqueLoading/opaqueLoading";

/* ==========================================================================
   TICKET MODULE  (single file)
   default export  -> TicketManagement   (list page)
   named exports   -> AddTicket          (create + edit page)
                      ViewTicket         (view / update status page)
   ========================================================================== */


// ======================= SHARED HELPERS & STYLES =======================

// ---------- constants ----------
const PRIORITIES = ["Low", "Medium", "High"];
const STATUSES = ["Open", "In Progress", "On Hold", "Closed"];

const NAVY = "#003b8e";
const CYAN = "#17c1e8";
const YELLOW = "#ffc107";
const PAGE_BG = "#f4f7fc";
const BORDER = "#dfe3ea";

const priorityColor = { High: "#dc3545", Medium: "#f5a623", Low: "#198754" };
const statusColor = {
    Open: "#198754",
    "In Progress": "#0d6efd",
    "On Hold": "#f5a623",
    Closed: "#dc3545",
};

// ---------- helpers ----------
const db = () => window.Platform.database;
const ok = (res) => res?.status === true;

const stripHtml = (html = "") =>
    String(html || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "28 Sep 2026, 03:15 PM"
const fmtDateTime = (d) => {
    if (!d) return "-";
    const x = new Date(d);
    if (isNaN(x)) return "-";
    let h = x.getHours();
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${String(x.getDate()).padStart(2, "0")} ${MONTHS[x.getMonth()]} ${x.getFullYear()}, ${String(h).padStart(2, "0")}:${String(x.getMinutes()).padStart(2, "0")} ${ampm}`;
};

// "2026-09-28 16:17"
const fmtCommentTime = (d) => {
    const x = new Date(d);
    if (isNaN(x)) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())} ${p(x.getHours())}:${p(x.getMinutes())}`;
};

const todayISO = () => {
    const x = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
};

const hrs = (n) => Number(n || 0).toFixed(2);

// ---------- shared styles ----------
const cardSx = {
    background: "#fff",
    borderRadius: "6px",
    boxShadow: "0 1px 6px rgba(20, 40, 90, 0.08)",
    p: { xs: 2, md: 2.5 },
};

const labelSx = { fontSize: 12.5, fontWeight: 500, color: NAVY, mb: 0.5 };

const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "3px", fontSize: 13, background: "#fff" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cfd6e2" },
    "& input, & textarea": { fontSize: 13 },
};

const primaryBtnSx = {
    background: NAVY,
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    borderRadius: "3px",
    boxShadow: "none",
    "&:hover": { background: "#002d6e", boxShadow: "none" },
};

const grayBtnSx = {
    background: "#d3dbe8",
    color: "#3a4a63",
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    borderRadius: "3px",
    boxShadow: "none",
    "&:hover": { background: "#c3cddd", boxShadow: "none" },
};

// ---------- layout ----------
function PageHeader({ title, crumbs = [] }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 24, color: NAVY, lineHeight: 1.2 }}>{title}</Typography>
            <Breadcrumbs separator="/" sx={{ fontSize: 13 }}>
                {crumbs.map((c, i) =>
                    c.to ? (
                        <RouterLink key={i} to={c.to} style={{ color: "#6c8ab5", textDecoration: "none", fontSize: 13 }}>
                            {c.label}
                        </RouterLink>
                    ) : (
                        <Typography key={i} sx={{ fontSize: 13, color: "#3a5f99" }}>
                            {c.label}
                        </Typography>
                    )
                )}
            </Breadcrumbs>
        </Box>
    );
}

const PageWrap = ({ children }) => (
    <Box sx={{ background: PAGE_BG, minHeight: "calc(100vh - 80px)", p: { xs: 1.5, md: 3 } }}>{children}</Box>
);

// ======================= VIEW TICKET PAGE =======================

// base url for uploaded files – change to your server/CDN url
const FILE_BASE = window.Platform?.fileBaseUrl || "";

const QUILL_SMALL = { toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]] };

const Badge = ({ text, color }) => (
    <Box component="span" sx={{ background: color, color: "#fff", fontSize: 10.5, fontWeight: 600, px: 1, py: 0.3, borderRadius: "4px" }}>
        {text}
    </Box>
);

const Row = ({ label, children }) => (
    <TableRow>
        <TableCell sx={{ width: "21%", fontWeight: 700, fontSize: 13, border: `1px solid ${BORDER}`, verticalAlign: "top", background: "#fff" }}>{label}</TableCell>
        <TableCell sx={{ fontSize: 13, border: `1px solid ${BORDER}`, background: "#fff" }}>{children}</TableCell>
    </TableRow>
);

const Field = ({ label, children }) => (
    <Box>
        <Typography sx={labelSx}>{label}</Typography>
        {children}
    </Box>
);

export function ViewTicket() {
    const { id } = useParams();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(false);

    // status form
    const [status, setStatus] = useState("");
    const [date, setDate] = useState(todayISO());
    const [workDuration, setWorkDuration] = useState("");
    const [workDescription, setWorkDescription] = useState("");

    // reopen form
    const [reopenReason, setReopenReason] = useState("");
    const [reopenStatus, setReopenStatus] = useState("In Progress");
    const [userData, setUserData] = useState(JSON.parse(localStorage.getItem('storedUserData')).user);

    // comment form
    const [comment, setComment] = useState("");
    const [isInternal, setIsInternal] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await db().getTicketById({ ticketId: id });
            if (ok(res)) {
                setTicket(res.data);
                setStatus(res.data.status);
            } else {
                window.NotificationUtils.showError(res?.message || "Ticket not found");
            }
        } catch (e) {
            console.error(e);
            window.NotificationUtils.showError("Error loading ticket");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // generic runner: call api -> toast -> reload
    const run = async (fn, after) => {
        try {
            setLoading(true);
            const res = await fn();
            if (ok(res)) {
                window.NotificationUtils.showSuccess(res.message);
                after && after();
                await load();
            } else {
                window.NotificationUtils.showError(res?.message || "Something went wrong");
            }
        } catch (e) {
            console.error(e);
            window.NotificationUtils.showError("Something went wrong!");
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = () => {
        if (status === "Closed" && !(Number(workDuration) > 0))
            return window.NotificationUtils.showError("Work duration is required to close a ticket");
        run(
            () => db().updateTicketStatus({ ticketId: id, status, date, workDuration, workDescription }),
            () => { setWorkDuration(""); setWorkDescription(""); }
        );
    };

    const reopen = () => {
        if (!reopenReason.trim()) return window.NotificationUtils.showError("Reopen reason is required");
        run(() => db().reopenTicket({ ticketId: id, reason: reopenReason, status: reopenStatus }), () => setReopenReason(""));
    };

    const addComment = () => {
        if (!comment.trim()) return window.NotificationUtils.showError("Write a comment first");
        run(() => db().addTicketComment({ ticketId: id, comment, isInternal }), () => { setComment(""); setIsInternal(false); });
    };

    if (!ticket) return <PageWrap>{loading && <OpaqueLoading />}</PageWrap>;

    const t = ticket;
    const atts = t.attachments || [];

    return (
        <PageWrap>
            {loading && <OpaqueLoading />}
            <PageHeader title={`View Ticket#${t.id}`} crumbs={[{ label: "Tickets", to: "/ticketManagement" }, { label: "Details" }]} />

            {/* Overview */}
            <Box sx={{ ...cardSx, p: 0, mb: 2.5 }}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 700, p: 2 }}>Ticket Overview</Typography>
                <Table size="small" sx={{ borderCollapse: "collapse" }}>
                    <TableBody>
                        <Row label="Module/Section">{t.module_name}</Row>
                        <Row label="Issue Description">
                            <Box sx={{ "& p": { my: 1 }, "& img": { maxWidth: "100%" } }}
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t.issue_description || "") }} />
                        </Row>
                        <Row label="Logged By User">{t.logged_by_user}</Row>
                        <Row label="Priority"><Badge text={t.priority} color={priorityColor[t.priority]} /></Row>
                        <Row label="Status"><Badge text={t.status} color={statusColor[t.status]} /></Row>
                        <Row label="Created By">{t.created_by_name || "N/A"}</Row>
                        <Row label="Assigned To">{t.assigned_to_name || "Unassigned"}</Row>
                        <Row label="Created At">{fmtDateTime(t.created_at)}</Row>
                        <Row label="QD Developer Remarks">{t.qd_developer_remarks || "-"}</Row>
                        <Row label="Additional Remarks">{t.additional_remarks || "-"}</Row>
                        <Row label="Last Updated">{fmtDateTime(t.updated_at)}</Row>
                        <Row label="Attached Artifacts">
                            {atts.length ? (
                                atts.map((a) => (
                                    <Box key={a.id}>
                                        <Link href={`${FILE_BASE}/${a.file_path}`} target="_blank" rel="noreferrer" sx={{ fontSize: 13 }}>
                                            {a.file_name}
                                        </Link>
                                    </Box>
                                ))
                            ) : (
                                <span style={{ color: "#6b7280" }}>No file uploaded</span>
                            )}
                        </Row>
                    </TableBody>
                </Table>
            </Box>

            {userData?.update_ticket == 1 && <Grid container spacing={2.5} alignItems="flex-start">
                {/* Left: Status + Reopen + Time logs */}
                <Grid item xs={12} md={7}>
                    <Box sx={{ ...cardSx, mb: 2.5 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 700, pb: 1.5, mb: 2, borderBottom: `1px solid ${BORDER}` }}>Status</Typography>

                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={4}>
                                <Field label="Update Status">
                                    <Select fullWidth size="small" value={status} onChange={(e) => setStatus(e.target.value)} sx={fieldSx}>
                                        {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                    </Select>
                                </Field>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Field label="Date">
                                    <TextField fullWidth size="small" type="date" value={date} onChange={(e) => setDate(e.target.value)} sx={fieldSx} />
                                </Field>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Field label="Work duration (hours)">
                                    <TextField fullWidth size="small" type="number" inputProps={{ step: "0.1", min: 0 }}
                                        placeholder="e.g. 1.5" value={workDuration} onChange={(e) => setWorkDuration(e.target.value)} sx={fieldSx} />
                                </Field>
                            </Grid>
                            <Grid item xs={12}>
                                <Field label="Work description">
                                    <Box sx={{ "& .ql-container": { minHeight: 90 }, "& .ql-toolbar, & .ql-container": { borderColor: "#cfd6e2" } }}>
                                        <ReactQuill theme="snow" modules={QUILL_SMALL} placeholder="Describe the work done (optional)..."
                                            value={workDescription} onChange={setWorkDescription} />
                                    </Box>
                                </Field>
                            </Grid>
                            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button variant="contained" sx={primaryBtnSx} onClick={updateStatus}>Update Status</Button>
                            </Grid>
                        </Grid>

                        {t.status === "Closed" && (
                            <Box sx={{ borderTop: `1px solid ${BORDER}`, mt: 2, pt: 2 }}>
                                <Typography sx={{ fontSize: 14, color: "#555", mb: 1 }}>Reopen Ticket</Typography>
                                <Field label="Reason">
                                    <TextField fullWidth multiline minRows={1} value={reopenReason}
                                        onChange={(e) => setReopenReason(e.target.value)} sx={fieldSx} />
                                </Field>
                                <Box sx={{ mt: 1.5 }}>
                                    <Field label="Set status to">
                                        <Select fullWidth size="small" value={reopenStatus} onChange={(e) => setReopenStatus(e.target.value)} sx={fieldSx}>
                                            {STATUSES.filter((s) => s !== "Closed").map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                        </Select>
                                    </Field>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                                    <Button variant="contained" onClick={reopen}
                                        sx={{ ...primaryBtnSx, background: YELLOW, color: "#000", "&:hover": { background: "#e6ac00" } }}>
                                        Reopen Ticket
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <Box sx={cardSx}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 700, pb: 1.5, mb: 1.5, borderBottom: `1px solid ${BORDER}` }}>Time Logs</Typography>
                        <Box sx={{ overflowX: "auto" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {["User", "Date", "Hours", "Description"].map((h) => (
                                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: 13, borderBottom: `1px solid ${BORDER}` }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(t.timeLogs || []).length === 0 && (
                                        <TableRow><TableCell colSpan={4} sx={{ fontSize: 13, color: "#6b7280" }}>No time logged yet.</TableCell></TableRow>
                                    )}
                                    {(t.timeLogs || []).map((l, i) => (
                                        <TableRow key={l.id} sx={{ background: i % 2 === 0 ? "#f1f1f1" : "#fff" }}>
                                            <TableCell sx={{ fontSize: 13, border: 0 }}>{l.user_name}</TableCell>
                                            <TableCell sx={{ fontSize: 13, border: 0 }}>{String(l.log_date).slice(0, 10)}</TableCell>
                                            <TableCell sx={{ fontSize: 13, border: 0 }}>{hrs(l.hours)}</TableCell>
                                            <TableCell sx={{ fontSize: 13, border: 0 }}>{stripHtml(l.work_description)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                </Grid>

                {/* Right: Comments */}
                <Grid item xs={12} md={5}>
                    <Box sx={cardSx}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 700, pb: 1.5, mb: 2, borderBottom: `1px solid ${BORDER}` }}>Comments</Typography>
                        <TextField fullWidth multiline minRows={1} placeholder="Write a comment..." value={comment}
                            onChange={(e) => setComment(e.target.value)} sx={fieldSx} />
                        <FormControlLabel
                            sx={{ mt: 0.5, "& .MuiTypography-root": { fontSize: 13.5 } }}
                            control={<Checkbox size="small" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} />}
                            label="Internal comment (visible to developer/admin only)"
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
                            <Button variant="contained" sx={primaryBtnSx} onClick={addComment}>Add Comment</Button>
                        </Box>

                        {(t.comments || []).map((c) => (
                            <Box key={c.id} sx={{ border: `1px solid ${BORDER}`, background: "#f7f7f7", borderRadius: "3px", p: 1.5, mb: 1.5 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                                        {c.user_name} {!!c.is_internal && <Badge text="Internal" color="#6c757d" />}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: "#6b7280" }}>{fmtCommentTime(c.created_at)}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: 14, mt: 0.5, whiteSpace: "pre-wrap" }}>{c.comment}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Grid>
            </Grid>}
        </PageWrap>
    );
}

// ======================= ADD / EDIT TICKET PAGE =======================

const EMPTY = {
    moduleName: "", description: "", loggedByUser: "", priority: "", status: "Open",
    assignedTo: "", qdRemarks: "", additionalRemarks: "",
};

const QUILL_MODULES = {
    toolbar: [
        [{ font: [] }, { size: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        [{ direction: "rtl" }, { align: [] }],
        ["link", "image", "video"],
        ["clean"],
    ],
};

const Label = ({ children, required }) => (
    <Typography sx={labelSx}>
        {children} {required && <span style={{ color: "red" }}>*</span>}
    </Typography>
);

export function AddTicket() {
    const navigate = useNavigate();
    const { id } = useParams(); // present => edit mode
    const isEdit = Boolean(id);
    const fileRef = useRef(null);

    const [form, setForm] = useState(EMPTY);
    const [files, setFiles] = useState([]);
    const [existingAtt, setExistingAtt] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    // users for "Assign to" (optional – only if your Platform has this API)
    useEffect(() => {
        (async () => {
            try {
                if (!db().getAllUsers) return;
                const res = await db().getAllUsers();
                if (res?.data) setUsers(res.data);
            } catch (e) { console.log(e); }
        })();
    }, []);

    // prefill on edit
    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                setLoading(true);
                const res = await db().getTicketById({ ticketId: id });
                if (!ok(res)) return window.NotificationUtils.showError(res?.message || "Ticket not found");
                const t = res.data;
                setForm({
                    moduleName: t.module_name || "",
                    description: t.issue_description || "",
                    loggedByUser: t.logged_by_user || "",
                    priority: t.priority || "",
                    status: t.status || "Open",
                    assignedTo: t.assigned_to || "",
                    qdRemarks: t.qd_developer_remarks || "",
                    additionalRemarks: t.additional_remarks || "",
                });
                setExistingAtt(t.attachments || []);
            } catch (e) {
                console.error(e);
                window.NotificationUtils.showError("Error loading ticket");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isEdit]);

    const reset = () => {
        setForm(EMPTY);
        setFiles([]);
        if (fileRef.current) fileRef.current.value = "";
    };

    const submit = async () => {
        if (!form.moduleName.trim()) return window.NotificationUtils.showError("Module name is required");
        if (!stripHtml(form.description)) return window.NotificationUtils.showError("Exact issue observed is required");
        if (!form.loggedByUser.trim()) return window.NotificationUtils.showError("Logged by user is required");
        if (!form.priority) return window.NotificationUtils.showError("Priority is required");

        const fd = new FormData();
        fd.append("moduleName", form.moduleName.trim());
        fd.append("description", form.description);
        fd.append("loggedByUser", form.loggedByUser.trim());
        fd.append("priority", form.priority);
        fd.append("status", form.status);
        fd.append("assignedTo", form.assignedTo || "");
        fd.append("qdRemarks", form.qdRemarks);
        fd.append("additionalRemarks", form.additionalRemarks);
        files.forEach((f) => fd.append("files", f)); // multer field name: change if your route uses another

        if (isEdit) {
            fd.append("ticketId", id);
            if (existingAtt.length) existingAtt.forEach((a) => fd.append("keepAttachmentIds", a.id));
            else fd.append("keepAttachmentIds", ""); // tells backend to delete all old attachments
        }

        try {
            setLoading(true);
            const res = await db().createNewTicket(fd);
            if (ok(res)) {
                window.NotificationUtils.showSuccess(res.message);
                navigate("/ticketManagement");
            } else {
                window.NotificationUtils.showError(res?.message || "Something went wrong");
            }
        } catch (e) {
            console.error(e);
            window.NotificationUtils.showError("Something went wrong!");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageWrap>
            {loading && <OpaqueLoading />}
            <PageHeader
                title={isEdit ? `Edit Ticket#${id}` : "Add Ticket"}
                crumbs={[{ label: "Home", to: "/dashboard" }, { label: "Ticket Management", to: "/ticketManagement" }]}
            />

            <Box sx={cardSx}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, pb: 1.5, mb: 2.5, borderBottom: "1px solid #dfe3ea" }}>
                    Ticket Information
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Label required>Module Name/ Section/ Item name</Label>
                        <TextField fullWidth size="small" placeholder="Enter module name" value={form.moduleName}
                            onChange={setF("moduleName")} sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Label>Screenshot/ Video File (If possible)</Label>
                        <Box sx={{ display: "flex", alignItems: "center", border: "1px solid #cfd6e2", borderRadius: "3px", height: 40, overflow: "hidden" }}>
                            <Button component="label" sx={{ ...grayBtnSx, background: "#eef1f6", color: "#222", height: "100%", borderRadius: 0, borderRight: "1px solid #cfd6e2", fontWeight: 400 }}>
                                Choose Files
                                <input ref={fileRef} hidden multiple type="file" accept="image/*,video/*,.pdf"
                                    onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                            </Button>
                            <Typography sx={{ fontSize: 13, px: 1.5 }} noWrap>
                                {files.length ? `${files.length} file(s) selected` : "No file chosen"}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                            Upload one or more (select with ctrl) screenshots, videos, or other files
                        </Typography>
                        {existingAtt.map((a) => (
                            <Box key={a.id} sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
                                <Typography sx={{ fontSize: 12.5 }}>{a.file_name}</Typography>
                                <Link component="button" type="button" sx={{ fontSize: 12, color: "#dc3545" }}
                                    onClick={() => setExistingAtt((p) => p.filter((x) => x.id !== a.id))}>
                                    Remove
                                </Link>
                            </Box>
                        ))}
                    </Grid>

                    <Grid item xs={12}>
                        <Label required>Exact Issue Observed</Label>
                        <Box sx={{ "& .ql-container": { minHeight: 150, fontSize: 14 }, "& .ql-toolbar, & .ql-container": { borderColor: "#cfd6e2" } }}>
                            <ReactQuill theme="snow" modules={QUILL_MODULES} value={form.description}
                                onChange={(v) => setForm((p) => ({ ...p, description: v }))} />
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Label required>Logged By User (enter your name)</Label>
                        <TextField fullWidth size="small" placeholder="Enter your name here" value={form.loggedByUser}
                            onChange={setF("loggedByUser")} sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Label required>Priority</Label>
                        <Select fullWidth size="small" displayEmpty value={form.priority} onChange={setF("priority")} sx={fieldSx}>
                            <MenuItem value="">--Select Priority--</MenuItem>
                            {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Label required>Status</Label>
                        {/* On edit, status is changed from the View page (updateTicketStatus) */}
                        <Select fullWidth size="small" value={form.status} onChange={setF("status")} sx={fieldSx} disabled={isEdit}>
                            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Label>Assign to</Label>
                        <Select fullWidth size="small" displayEmpty value={form.assignedTo} onChange={setF("assignedTo")} sx={fieldSx}>
                            {/* <MenuItem value="">--Select--</MenuItem> */}
                            {/* {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name || u.username}</MenuItem>)} */}
                            <MenuItem key={1} value={"developer"}>developer</MenuItem>
                        </Select>
                    </Grid>

                    <Grid item xs={12}>
                        <Label>Remark by QD Team</Label>
                        <TextField fullWidth multiline minRows={4} placeholder="Enter remarks from QD team"
                            value={form.qdRemarks} onChange={setF("qdRemarks")} sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12}>
                        <Label>Further Remarks</Label>
                        <TextField fullWidth multiline minRows={1} placeholder="Enter any additional remarks or updates"
                            value={form.additionalRemarks} onChange={setF("additionalRemarks")} sx={fieldSx} />
                    </Grid>

                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button variant="contained" sx={primaryBtnSx} onClick={submit}>{isEdit ? "Update" : "Submit"}</Button>
                        {!isEdit && <Button variant="contained" sx={grayBtnSx} onClick={reset}>Reset</Button>}
                        {isEdit && <Button variant="contained" sx={grayBtnSx} onClick={() => navigate("/ticketManagement")}>Cancel</Button>}
                    </Grid>
                </Grid>
            </Box>
        </PageWrap>
    );
}

// ======================= TICKET LIST PAGE (default export) =======================

const EMPTY_FILTERS = { search: "", priority: "", status: "", fromDate: "", toDate: "" };

const headCell = {
    fontSize: 12, fontWeight: 700, color: "#111", border: `1px solid ${BORDER}`,
    background: "#fff", py: 1.5,
};
const bodyCell = { fontSize: 11.5, color: "#222", border: `1px solid ${BORDER}`, py: 1.5 };

function SummaryCard({ title, items }) {
    return (
        <Box sx={{ ...cardSx, textAlign: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, color: CYAN, mb: 2 }}>
                <CalendarMonthIcon sx={{ fontSize: 18, color: "#6c8ab5" }} />
                <Typography sx={{ fontSize: 16, fontWeight: 500, color: CYAN }}>{title}</Typography>
            </Box>
            <Grid container spacing={1}>
                {items.map((it) => (
                    <Grid item xs={4} key={it.label}>
                        <Typography sx={{ fontSize: 13.5, color: "#444" }}>{it.label}</Typography>
                        <Typography sx={{ fontSize: 24, color: it.green ? "#198754" : "#222", mt: 0.5 }}>
                            {hrs(it.value)} hrs
                        </Typography>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

export default function TicketManagement() {
    const navigate = useNavigate();

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [applied, setApplied] = useState(EMPTY_FILTERS);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({
        previous: { allotted: 0, used: 0, carryForward: 0 },
        current: { available: 0, used: 0, remaining: 0 },
    });

    const fetchTickets = useCallback(async () => {
        try {
            setLoading(true);
            const res = await db().getAllTickets({ ...applied, page, limit });
            if (ok(res)) {
                setRows(res.data || []);
                setTotal(res.data1 || 0);
            } else {
                window.NotificationUtils.showError(res?.message || "Something went wrong");
            }
        } catch (e) {
            console.error(e);
            window.NotificationUtils.showError("Error fetching tickets");
        } finally {
            setLoading(false);
        }
    }, [applied, page, limit]);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    // Hours summary: needs a backend API (see notes). Safe fallback if it doesn't exist yet.
    useEffect(() => {
        (async () => {
            try {
                if (!db().getTicketHoursSummary) return;
                const res = await db().getTicketHoursSummary();
                if (ok(res) && res.data) setSummary(res.data);
            } catch (e) { console.log("summary not available", e); }
        })();
    }, []);

    const applyFilters = () => { setPage(1); setApplied(filters); };
    const clearFilters = () => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(1); };
    const setF = (k) => (e) => setFilters((p) => ({ ...p, [k]: e.target.value }));

    return (
        <PageWrap>
            {loading && <OpaqueLoading />}
            <PageHeader title="Ticket Management"
            //  crumbs={[{ label: "Home", to: "/dashboard" }, { label: "Tickets" }]}
            />

            {/* Month summary */}
            {/* <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <SummaryCard
                        title="Previous Month Summary"
                        items={[
                            { label: "Allotted", value: summary.previous.allotted },
                            { label: "Used", value: summary.previous.used },
                            { label: "Carry Forward", value: summary.previous.carryForward, green: true },
                        ]}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <SummaryCard
                        title="Current Month Summary"
                        items={[
                            { label: "Available (Allotted + CF)", value: summary.current.available },
                            { label: "Used", value: summary.current.used },
                            { label: "Remaining", value: summary.current.remaining, green: true },
                        ]}
                    />
                </Grid>
            </Grid> */}

            {/* Filters */}
            <Box sx={{ ...cardSx, mb: 3, pb: 4 }}>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" placeholder="Search Issue" value={filters.search}
                            onChange={setF("search")} sx={fieldSx}
                            onKeyDown={(e) => e.key === "Enter" && applyFilters()} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Select fullWidth size="small" displayEmpty value={filters.priority} onChange={setF("priority")} sx={fieldSx}>
                            <MenuItem value="">All Priorities</MenuItem>
                            {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Select fullWidth size="small" displayEmpty value={filters.status} onChange={setF("status")} sx={fieldSx}>
                            <MenuItem value="">All Status</MenuItem>
                            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" type="date" value={filters.fromDate} onChange={setF("fromDate")} sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" type="date" value={filters.toDate} onChange={setF("toDate")} sx={fieldSx} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button fullWidth variant="contained" sx={{ ...primaryBtnSx, height: 40 }} onClick={applyFilters}>Filter</Button>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button fullWidth variant="contained" sx={{ ...grayBtnSx, height: 40 }} onClick={clearFilters}>Clear</Button>
                    </Grid>
                </Grid>
            </Box>

            {/* Table */}
            <Box sx={{ ...cardSx, pb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, pb: 2, mb: 2, borderBottom: "1px solid #c9ced8" }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>All Tickets</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography sx={{ fontSize: 14, color: "#333", mr: 1 }}>Per Page Records:</Typography>
                        <Select size="small" value={limit} sx={{ ...fieldSx, minWidth: 80 }}
                            onChange={(e) => { setLimit(e.target.value); setPage(1); }}>
                            {[10, 20, 30, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </Select>
                        <Button variant="contained" sx={{ ...primaryBtnSx, height: 40, ml: 0.5 }}
                            onClick={() => navigate("/ticketManagement/create")}>
                            Create Ticket
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 950, borderCollapse: "collapse" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headCell}>S no.</TableCell>
                                <TableCell sx={headCell}>Ticket Id</TableCell>
                                <TableCell sx={headCell}>Issue</TableCell>
                                <TableCell sx={headCell}>Priority</TableCell>
                                <TableCell sx={headCell}>Status</TableCell>
                                <TableCell sx={headCell}>Creator</TableCell>
                                <TableCell sx={headCell}>Work Duration (hrs)</TableCell>
                                <TableCell sx={headCell}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ ...bodyCell, py: 4 }}>
                                        No tickets found. Change the filters or create a new ticket.
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((t, i) => {
                                const issue = stripHtml(t.issue_description);
                                return (
                                    <TableRow key={t.id} sx={{ background: i % 2 === 0 ? "#f1f1f1" : "#fff" }}>
                                        <TableCell sx={bodyCell}>{(page - 1) * limit + i + 1}.</TableCell>
                                        <TableCell sx={bodyCell}>Ticket#{t.id}</TableCell>
                                        <TableCell sx={{ ...bodyCell, maxWidth: 560 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                                <Typography noWrap sx={{ fontSize: 11.5, maxWidth: 480 }}>{issue}</Typography>
                                                <Tooltip title={<Box sx={{ fontSize: 12, maxWidth: 360 }}>{t.module_name ? `${t.module_name}: ` : ""}{issue}</Box>}>
                                                    <InfoOutlinedIcon sx={{ fontSize: 20, color: "#222", flexShrink: 0 }} />
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ ...bodyCell, color: priorityColor[t.priority], fontWeight: 600 }}>{t.priority}</TableCell>
                                        <TableCell sx={{ ...bodyCell, color: statusColor[t.status], fontWeight: 500 }}>{t.status}</TableCell>
                                        <TableCell sx={bodyCell}>{t.created_by_name || t.logged_by_user || "N/A"}</TableCell>
                                        <TableCell sx={bodyCell}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                {hrs(t.total_work_hours)}
                                                {!!Number(t.hasDeveloperRemark) && (
                                                    <Chip label="Developer Remark" size="small"
                                                        sx={{ background: "#198754", color: "#fff", fontSize: 10, height: 18, fontWeight: 600 }} />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={bodyCell}>
                                            <Box sx={{ display: "flex", gap: 0.5 }}>
                                                <Tooltip title="Edit ticket">
                                                    <IconButton size="small" onClick={() => navigate(`/ticketManagement/edit/${t.id}`)}
                                                        sx={{ background: YELLOW, borderRadius: "4px", width: 28, height: 28, "&:hover": { background: "#e6ac00" } }}>
                                                        <EditIcon sx={{ fontSize: 15, color: "#000" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="View ticket">
                                                    <IconButton size="small" onClick={() => navigate(`/ticketManagement/view/${t.id}`)}
                                                        sx={{ background: CYAN, borderRadius: "4px", width: 28, height: 28, "&:hover": { background: "#10a8cc" } }}>
                                                        <VisibilityIcon sx={{ fontSize: 16, color: "#000" }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>

                <Box sx={{ mt: 3, pl: 1 }}>
                    {/* <Pagination
                        count={Math.max(Math.ceil(total / limit), 1)}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        shape="circular"
                        sx={{color:"#fff",
                            "& .Mui-selected": { background: `${NAVY} !important`, color: "#fff" },
                            "& .MuiPaginationItem-root": { boxShadow: "0 1px 4px rgba(0,0,0,0.12)", color: "NAVY" },
                        }}
                    /> */}
                    <Pagination
                        count={Math.max(Math.ceil(total / limit), 1)}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        shape="circular"
                        sx={{
                            color: "#fff",
                            "& .MuiPaginationItem-root": {
                                boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                                color: NAVY,
                            },
                            "& .MuiPaginationItem-root.Mui-selected": {
                                backgroundColor: `${NAVY} !important`,
                                color: "#fff !important",
                            },
                        }}
                    />
                </Box>
            </Box>
        </PageWrap>
    );
}